import React from 'react';
import Grid from '@mui/material/GridLegacy';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography, FormControl, Select, MenuItem, IconButton } from '@mui/material';
import { DIALOG_CONTENT_SX } from '../../../theme';
import CategoryIconByName from '../../../components/common/CategoryIconByName';
import { CATEGORY_ICON_NAMES, getLucideComponentName } from '../../../utils/categoryIcons';
import * as LucideIcons from 'lucide-react';

export default function CategoryDialog({
  open,
  onClose,
  form,
  setForm,
  onSave,
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)' } }}
    >
      <DialogTitle sx={{
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        color: 'white',
        fontWeight: 700,
        py: 3,
        px: 4
      }}>
        {form._id ? 'Edit Category' : 'Create New Category'}
      </DialogTitle>
      <DialogContent sx={DIALOG_CONTENT_SX}>
        <Grid container spacing={3} sx={{ pt: 1 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Category Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Hidden Gem, Restaurant, Cafe"
              required
              helperText="Use normal names (e.g. Hidden Gem, Restaurant, Cafe)"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Icon</Typography>
            <Box sx={{ maxHeight: 220, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
              <Grid container spacing={0.5}>
                {CATEGORY_ICON_NAMES.map((key) => {
                  const IconComp = LucideIcons[getLucideComponentName(key)];
                  if (!IconComp) return null;
                  const selected = form.icon === key;
                  return (
                    <Grid item key={key}>
                      <IconButton
                        onClick={() => setForm({ ...form, icon: key })}
                        sx={{
                          bgcolor: selected ? 'primary.main' : 'transparent',
                          color: selected ? 'primary.contrastText' : 'text.primary',
                          '&:hover': { bgcolor: selected ? 'primary.dark' : 'action.hover' }
                        }}
                        size="small"
                        title={key}
                      >
                        <IconComp size={22} />
                      </IconButton>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
            {form.icon && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: 1, bgcolor: form.color || '#667eea', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                  <CategoryIconByName iconName={form.icon} size={24} color="white" />
                </Box>
                <Typography variant="body2" color="text.secondary">Selected: {form.icon}</Typography>
              </Box>
            )}
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description (Optional)"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
              <Select
                label="Color"
                value={form.color || '#667eea'}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
                displayEmpty
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: selected, border: '1px solid rgba(0, 0, 0, 0.2)' }} />
                    <span>{selected}</span>
                  </Box>
                )}
              >
                {['#667eea', '#4facfe', '#43e97b', '#f093fb', '#f5576c', '#ffa726', '#26c6da', '#ab47bc'].map((color) => (
                  <MenuItem key={color} value={color}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: color, border: '1px solid rgba(0, 0, 0, 0.2)' }} />
                      {color}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Sort Order"
              type="number"
              value={form.sortOrder}
              onChange={(e) => setForm({ ...form, sortOrder: parseInt(e.target.value) || 0 })}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 3, bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
        <Button onClick={onClose} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500, px: 3 }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={!form.name || !form.icon}
          sx={{
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            boxShadow: '0 4px 15px rgba(79, 172, 254, 0.4)',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            '&:hover': {
              background: 'linear-gradient(135deg, #00f2fe 0%, #4facfe 100%)',
              boxShadow: '0 6px 20px rgba(79, 172, 254, 0.5)'
            },
            '&:disabled': { background: 'rgba(0, 0, 0, 0.12)' }
          }}
        >
          {form._id ? 'Update Category' : 'Create Category'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
